import "@/App.css";
import { useState } from "react";
import { ReactLenis, useLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Facebook, Phone, Mail, MapPin, ArrowRight, Wind, Headset, Droplets, Camera, Layers, Menu as MenuIcon, X } from "lucide-react";
import EyeJourney from "./components/EyeJourney";
import OctSection from "./components/OctSection";
import { BookingProvider, useBooking } from "./BookingContext";
import { LINKS, SERVICES, BRANDS, TEAM, MISSION, TEAM_INTRO, CONTACT, HOURS, LOGOS, BRANDS_INTRO } from "./data";

const BOOK_TITLE = "Book Your Appointment";
const ORDER_TITLE = "Order Your Contacts";

const NAV = [
  { label: "The Eye", target: 0 },
  { label: "About", target: "#about" },
  { label: "Technology", target: "#technology" },
  { label: "Pricing", target: "#pricing" },
  { label: "Eyewear", target: "#eyewear" },
  { label: "Visit", target: "#visit" },
];

function QuickNav() {
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const booking = useBooking();

  const go = (target) => {
    setOpen(false);
    if (lenis) lenis.scrollTo(target, { offset: 0, duration: 1.4 });
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="menu-open-btn"
        aria-label="Open menu"
        className="fixed top-5 right-5 md:top-7 md:right-8 z-[110] flex items-center gap-2 rounded-full bg-[#0F172A]/70 backdrop-blur-md text-white px-4 py-2.5 md:px-5 md:py-3 hover:bg-[#0F172A] transition-colors duration-300"
      >
        <MenuIcon size={16} />
        <span className="text-[11px] uppercase tracking-[0.25em] font-medium">Menu</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[120] bg-[#0F172A]"
            data-testid="menu-overlay"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col">
              <div className="flex items-center justify-between py-6 md:py-8">
                <span className="font-display text-xl md:text-2xl text-white font-light">West Springs Optometry</span>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="menu-close-btn"
                  aria-label="Close menu"
                  className="flex items-center gap-2 rounded-full border border-white/20 text-white px-4 py-2.5 hover:bg-white/10 transition-colors"
                >
                  <X size={16} />
                  <span className="text-[11px] uppercase tracking-[0.25em]">Close</span>
                </button>
              </div>

              <nav className="flex-1 flex flex-col justify-center gap-2 md:gap-3">
                {NAV.map((item, i) => (
                  <motion.button
                    key={item.label}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.5 }}
                    onClick={() => go(item.target)}
                    data-testid={`menu-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="group text-left font-display font-light text-white text-5xl md:text-7xl leading-tight hover:text-[#F59E0B] transition-colors duration-300 flex items-center gap-4"
                  >
                    <span className="text-xs font-sans text-white/30 group-hover:text-[#F59E0B] transition-colors">0{i + 1}</span>
                    {item.label}
                  </motion.button>
                ))}
              </nav>

              <div className="py-8 md:py-10 flex flex-col sm:flex-row gap-4 border-t border-white/10">
                <a
                  href={LINKS.bookOnlineHref}
                  onClick={(e) => { e.preventDefault(); setOpen(false); booking.open(LINKS.bookOnline, BOOK_TITLE); }}
                  data-testid="menu-book-online-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F59E0B] text-[#1A1D20] px-8 py-4 text-sm font-medium hover:bg-white transition-colors cursor-pointer"
                >
                  Book Online <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const Reveal = ({ children, className, delay = 0, testid }) => (
  <motion.div
    variants={reveal}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.3 }}
    transition={{ delay }}
    className={className}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.4 2.3 1.9 4 4.1 4.2v3.1c-1.5.1-2.9-.3-4.1-1.1v6.3c0 3.3-2.7 5.9-6 5.4-2.5-.4-4.4-2.5-4.6-5-.2-2.9 2.1-5.4 5-5.4.3 0 .6 0 .9.1v3.2c-.3-.1-.6-.2-.9-.2-1.3 0-2.3 1.2-2 2.5.2.9 1 1.6 1.9 1.6 1.1.1 2.1-.8 2.1-1.9V3h3.6z" />
  </svg>
);

const SectionLabel = ({ children, dark }) => (
  <p className={`text-[10px] md:text-xs uppercase tracking-[0.35em] mb-5 ${dark ? "text-[#F59E0B]" : "text-[#D97706]"}`}>
    {children}
  </p>
);

function AboutSection() {
  return (
    <section id="about" className="bg-[#F4F1EB] py-24 md:py-32" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-3xl mb-16 md:mb-20">
          <SectionLabel>About Us · Meet the Team</SectionLabel>
          <h2 className="font-display font-light text-4xl md:text-6xl text-[#1A1D20] leading-tight mb-6">
            Personalized care, from people who know eyes.
          </h2>
          <p className="text-[#4A5568] font-light text-base md:text-lg leading-relaxed mb-5">{TEAM_INTRO}</p>
          <p className="text-[#4A5568] font-light text-base md:text-lg leading-relaxed">{MISSION}</p>
        </Reveal>

        {/* Featured — Dr. Elsaghir up front */}
        <Reveal testid="team-card-0">
          <div className="grid md:grid-cols-5 gap-0 rounded-3xl bg-[#FDFCF8] border border-[#E7E2D7] overflow-hidden">
            <div className="md:col-span-2 bg-[#0F172A] aspect-[4/5] md:aspect-auto md:min-h-[520px]">
              <img src={TEAM[0].img} alt={TEAM[0].name} className="h-full w-full object-cover object-top" />
            </div>
            <div className="md:col-span-3 p-8 md:p-12 flex flex-col justify-center">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D97706] mb-3">{TEAM[0].role}</p>
              <h3 className="font-display text-4xl md:text-5xl text-[#1A1D20] font-light mb-5">{TEAM[0].name}</h3>
              <div className="space-y-3 text-[#4A5568] font-light text-sm md:text-[15px] leading-relaxed">
                {TEAM[0].bioParagraphs.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
          {TEAM.slice(1).map((m, i) => (
            <Reveal key={m.name} className="mt-10 md:mt-14" delay={i * 0.08} testid={`team-card-${i + 1}`}>
              <div className="rounded-3xl bg-[#FDFCF8] border border-[#E7E2D7] overflow-hidden h-full flex flex-col sm:flex-row">
                <div className="bg-[#0F172A] flex items-center justify-center sm:w-40 aspect-video sm:aspect-auto shrink-0">
                  <span className="font-display text-5xl text-[#F59E0B] font-light">{m.initials}</span>
                </div>
                <div className="p-6 md:p-7 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#D97706] mb-2">{m.role}</p>
                  <h3 className="font-display text-2xl md:text-3xl text-[#1A1D20] font-light mb-3">{m.name}</h3>
                  <p className="text-[#4A5568] font-light text-sm leading-relaxed">{m.bio}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TechSection() {
  return (
    <section id="technology" className="bg-[#FDFCF8] py-24 md:py-32" data-testid="tech-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl mb-14 md:mb-16">
          <SectionLabel>Technology</SectionLabel>
          <h2 className="font-display font-light text-4xl md:text-6xl text-[#1A1D20] leading-tight">
            Advanced imaging, in plain language.
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-6 md:mb-8">
          <Reveal className="rounded-3xl bg-[#F4F1EB] p-8 md:p-12 border border-[#E7E2D7]" testid="optos-card">
            <div className="flex items-center gap-3 text-[#D97706] mb-5">
              <Camera size={20} />
              <span className="text-xs uppercase tracking-[0.3em]">Optos California</span>
            </div>
            <h3 className="font-display font-light text-3xl md:text-4xl text-[#1A1D20] leading-snug mb-4">
              200° of your retina in a single scan.
            </h3>
            <p className="text-[#4A5568] font-light text-sm md:text-base">
              Ultra-widefield imaging takes a detailed digital photograph of the retina — the light-sensitive
              tissue at the back of your eye. Where traditional cameras capture only a small portion, the Optos
              captures up to 200 degrees — about 82% of the retina — in one image.
            </p>
          </Reveal>

          <Reveal className="rounded-3xl bg-[#F4F1EB] p-8 md:p-12 border border-[#E7E2D7]" delay={0.1} testid="oct-card">
            <div className="flex items-center gap-3 text-[#D97706] mb-5">
              <Layers size={20} />
              <span className="text-xs uppercase tracking-[0.3em]">OCT · Optical Coherence Tomography</span>
            </div>
            <h3 className="font-display font-light text-3xl md:text-4xl text-[#1A1D20] leading-snug mb-4">
              An "optical ultrasound" of every retinal layer.
            </h3>
            <p className="text-[#4A5568] font-light text-sm md:text-base">
              OCT captures a cross-sectional view that lets us see each layer of the retina and measure tissue
              thickness — catching subtle early changes from glaucoma, macular degeneration, and diabetic eye
              disease before they'd appear on a standard photograph.
            </p>
          </Reveal>
        </div>

        {/* iCare — biggest type */}
        <Reveal className="rounded-3xl bg-[#0F172A] px-8 md:px-16 py-16 md:py-24 relative overflow-hidden" testid="icare-card">
          <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 text-[#F59E0B] mb-6">
                <Wind size={20} />
                <span className="text-xs uppercase tracking-[0.3em]">iCare Tonometer</span>
              </div>
              <h2 className="font-display font-light text-white text-6xl md:text-8xl leading-[0.9]">
                Eye pressure.<br />No puff. Ever.
              </h2>
            </div>
            <p className="text-white/70 font-light text-base max-w-xs">
              A modern device that measures your intraocular pressure — an important part of screening for
              glaucoma — without the dreaded air puff.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-6 md:mt-8">
          <Reveal className="rounded-3xl bg-[#F4F1EB] p-8 md:p-12 border border-[#E7E2D7]" testid="retinalogik-card">
            <div className="flex items-center gap-3 text-[#D97706] mb-5">
              <Headset size={20} />
              <span className="text-xs uppercase tracking-[0.3em]">Retinalogik Virtual Visual Field</span>
            </div>
            <h3 className="font-display font-light text-3xl md:text-4xl text-[#1A1D20] leading-snug mb-4">
              Your peripheral vision test is now a VR headset.
            </h3>
            <p className="text-[#4A5568] font-light text-sm md:text-base">
              An advanced, headset-based test that maps your side vision to detect and monitor glaucoma and
              other conditions — more comfortable and efficient than traditional machines.
            </p>
          </Reveal>

          <Reveal className="rounded-3xl bg-[#F4F1EB] p-8 md:p-12 border border-[#E7E2D7]" delay={0.1} testid="meibography-card">
            <div className="flex items-center gap-3 text-[#D97706] mb-5">
              <Droplets size={20} />
              <span className="text-xs uppercase tracking-[0.3em]">Meibography · Dry Eye</span>
            </div>
            <h3 className="font-display font-light text-3xl md:text-4xl text-[#1A1D20] leading-snug mb-4">
              Imaging the oil glands behind healthy, comfortable tears.
            </h3>
            <p className="text-[#4A5568] font-light text-sm md:text-base">
              We picture the meibomian glands along your eyelids so we can diagnose and manage dry eye and
              gland dysfunction early — long before it becomes uncomfortable.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="bg-[#FDFCF8] py-24 md:py-32 border-t border-[#E7E2D7]" data-testid="pricing-section">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Reveal className="mb-14 md:mb-20 text-center">
          <SectionLabel>Honest Pricing</SectionLabel>
          <h2 className="font-display font-light text-4xl md:text-6xl text-[#1A1D20]">Services &amp; Pricing</h2>
          <p className="mt-5 text-[#4A5568] font-light max-w-lg mx-auto">
            Transparent from the start — including exactly what Alberta Health Care covers.
          </p>
        </Reveal>

        <div className="divide-y divide-[#E7E2D7]" data-testid="pricing-table">
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-3 md:gap-8 py-7 md:py-9 items-start">
                <div className="md:col-span-1 text-[#D97706] font-display text-xl">{s.n}</div>
                <div className="md:col-span-7">
                  <h3 className="text-lg md:text-xl text-[#1A1D20] font-medium">{s.title}</h3>
                  <p className="mt-2 text-[#4A5568] font-light text-sm leading-relaxed">{s.detail}</p>
                  {s.note && <p className="mt-2 text-[#D97706] text-sm">{s.note}</p>}
                </div>
                <div className="md:col-span-4 md:text-right">
                  <span className="font-display text-2xl md:text-3xl text-[#1A1D20] font-light">{s.price}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 rounded-2xl bg-[#F4F1EB] border border-[#E7E2D7] p-6 text-sm text-[#4A5568] font-light">
          <span className="text-[#1A1D20] font-medium">Alberta Health Care coverage:</span> Children under 5 are
          fully covered. Seniors (65+) and children 6–18 are covered with the copays listed above.
        </Reveal>
      </div>
    </section>
  );
}

function Eyewear() {
  return (
    <section id="eyewear" className="bg-[#0F172A] py-24 md:py-32" data-testid="eyewear-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="mb-14 md:mb-20 max-w-2xl">
          <SectionLabel dark>The Boutique</SectionLabel>
          <h2 className="font-display font-light text-4xl md:text-6xl text-white leading-tight mb-6">
            The frames we keep on the shelf.
          </h2>
          <p className="text-white/60 font-light text-sm md:text-base">{BRANDS_INTRO}</p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {BRANDS.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.06}>
              <div
                className="brand-card group rounded-2xl overflow-hidden bg-[#F4F1EB] border border-white/10 h-full"
                data-testid={`brand-card-${b.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="overflow-hidden aspect-[4/3] bg-white">
                  <img src={b.img} alt={`${b.name} eyewear`} className="h-full w-full object-cover" />
                </div>
                <div className="p-6 md:p-7">
                  <h3 className="font-display text-2xl md:text-3xl text-[#1A1D20] font-light mb-2">{b.name}</h3>
                  <p className="text-[#4A5568] font-light text-sm leading-relaxed">{b.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 md:mt-24 pt-12 border-t border-white/10" testid="all-brands">
          <p className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#F59E0B] mb-10 text-center">
            Working With the Best
          </p>
          <div className="marquee-track relative overflow-hidden rounded-3xl bg-black py-10 md:py-12 [mask-image:linear-gradient(to_right,transparent,#000_8%,#000_92%,transparent)]">
            <div className="marquee items-center gap-16 md:gap-24">
              {[...LOGOS, ...LOGOS].map((logo, i) => (
                <div
                  key={`${logo.name}-${i}`}
                  data-testid={i < LOGOS.length ? `brand-logo-${logo.name.toLowerCase().replace(/[\s&.]+/g, "-")}` : undefined}
                  title={logo.name}
                  className="logo-chip shrink-0 flex items-center justify-center h-24 md:h-32"
                >
                  <img
                    src={logo.url}
                    alt={`${logo.name} logo`}
                    loading="lazy"
                    className="logo-white max-h-20 md:max-h-28 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function OrderContacts() {
  return null;
}

function Visit() {
  return (
    <section id="visit" className="bg-[#FDFCF8] py-24 md:py-32" data-testid="visit-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        <Reveal>
          <SectionLabel>Visit Us</SectionLabel>
          <h2 className="font-display font-light text-4xl md:text-6xl text-[#1A1D20] leading-tight mb-6">
            West Springs, Calgary.
          </h2>
          <p className="text-[#4A5568] font-light text-base md:text-lg mb-8">
            Accepting new patients — walk-ins welcome, and same-day appointments available.
          </p>
          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-[#D97706] mt-0.5 shrink-0" />
              <span className="text-[#1A1D20]">
                <span className="font-medium">West Springs Optometry</span>
                <br />
                {CONTACT.addressLine1}
                <br />
                {CONTACT.addressLine2}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={18} className="text-[#D97706] shrink-0" />
              <a href={CONTACT.phoneHref} data-testid="visit-phone" className="text-[#1A1D20] hover:text-[#D97706] transition-colors">
                {CONTACT.phone}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Mail size={18} className="text-[#D97706] shrink-0" />
              <a href={`mailto:${CONTACT.email}`} data-testid="visit-email" className="text-[#1A1D20] hover:text-[#D97706] transition-colors">
                {CONTACT.email}
              </a>
            </div>
            <div className="rounded-xl border border-[#E7E2D7] bg-[#F4F1EB] p-5" data-testid="hours-block">
              <p className="text-[#D97706] text-xs uppercase tracking-[0.2em] mb-3">Hours</p>
              <ul className="space-y-2">
                {HOURS.map((h) => (
                  <li key={h.day} className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-[#1A1D20]">{h.day}</span>
                    <span className={`font-light ${h.time === "Closed" ? "text-[#94896F]" : "text-[#4A5568]"}`}>
                      {h.time}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="rounded-3xl overflow-hidden border border-[#E7E2D7] h-[340px] md:h-[440px]">
          <iframe
            title="West Springs Optometry location"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&ll=${CONTACT.mapLat},${CONTACT.mapLng}&z=17&output=embed`}
            className="w-full h-full"
            loading="lazy"
            data-testid="map-embed"
          />
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  const booking = useBooking();
  return (
    <footer className="bg-[#0F172A] pt-24 md:pt-32 pb-32 md:pb-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="text-center mb-16">
          <h2 className="font-display font-light text-white text-5xl md:text-8xl leading-[0.95] mb-10">
            Ready when you are.
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => booking.open(LINKS.bookOnline, BOOK_TITLE)}
              data-testid="book-online-footer-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#F59E0B] text-[#1A1D20] px-10 py-4 text-base font-medium hover:bg-white transition-colors duration-300"
            >
              Book Online <ArrowRight size={18} />
            </button>
            <a
              href={CONTACT.phoneHref}
              data-testid="call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-10 py-4 text-base font-light hover:bg-white/10 transition-colors duration-300"
            >
              <Phone size={16} /> {CONTACT.phone}
            </a>
          </div>
        </Reveal>

        <div className="border-t border-white/10 pt-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="font-display text-xl text-white/90">West Springs Optometry</p>
          <div className="flex items-center gap-5 text-white/70">
            <a href={LINKS.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram" className="hover:text-[#F59E0B] transition-colors">
              <Instagram size={20} />
            </a>
            <a href={LINKS.tiktok} target="_blank" rel="noreferrer" data-testid="social-tiktok" className="hover:text-[#F59E0B] transition-colors">
              <TikTokIcon size={20} />
            </a>
            <a href={LINKS.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook" className="hover:text-[#F59E0B] transition-colors">
              <Facebook size={20} />
            </a>
          </div>
          <p className="text-white/40 text-xs">@westspringsoptometry</p>
        </div>
      </div>
    </footer>
  );
}

function MobileBookBar() {
  const booking = useBooking();
  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-[100] p-3 bg-[#FDFCF8]/90 backdrop-blur-xl border-t border-[#E7E2D7]"
      data-testid="mobile-book-bar"
    >
      <a
        href={LINKS.bookOnlineHref}
        onClick={(e) => { e.preventDefault(); booking.open(LINKS.bookOnline, BOOK_TITLE); }}
        data-testid="book-online-mobile-btn"
        className="w-full flex items-center justify-center gap-2 rounded-full bg-[#F59E0B] text-[#1A1D20] py-3.5 text-sm font-semibold cursor-pointer"
      >
        Book Online <ArrowRight size={16} />
      </a>
    </div>
  );
}

function App() {
  return (
    <BookingProvider>
      <ReactLenis root>
        <div className="App bg-[#FDFCF8]">
          <QuickNav />
          <EyeJourney />
          <OctSection />
          <AboutSection />
          <TechSection />
          <Pricing />
          <Eyewear />
          <OrderContacts />
          <Visit />
          <Footer />
          <MobileBookBar />
        </div>
      </ReactLenis>
    </BookingProvider>
  );
}

export default App;
 App;
